import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, Wrench, Filter, X, AlertTriangle, CheckCircle, XCircle, AlertCircle,
  ChevronLeft, ChevronRight, Link2, Monitor, Power, PowerOff,
  ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const VirtToolsReport = () => {
  const [loading, setLoading] = useState(true);
  const [vms, setVMs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'tools_status', dir: 'asc' });
  
  // Active stat filter
  const [activeStatFilter, setActiveStatFilter] = useState('issues');

  const fetchVMs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      const res = await api.get(`/reports/virt/tools?${params}`);
      setVMs(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar reporte de Tools');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/reports/virt/tools/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchVMs, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Stat card click handler
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    switch (filterType) {
      case 'not_installed':
        setFilters({ search: '', status: 'not_installed' });
        break;
      case 'old':
        setFilters({ search: '', status: 'old' });
        break;
      case 'not_running':
        setFilters({ search: '', status: 'not_running' });
        break;
      case 'ok':
        setFilters({ search: '', status: 'ok' });
        break;
      default:
        // Por defecto mostrar solo con problemas
        setFilters({ search: '', status: '' });
        break;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'toolsNotInstalled':
        return <Badge className="bg-red-500 text-white">No instalado</Badge>;
      case 'toolsOld':
        return <Badge className="bg-yellow-500 text-black">Desactualizado</Badge>;
      case 'toolsNotRunning':
        return <Badge className="bg-orange-500 text-white">No corriendo</Badge>;
      case 'toolsOk':
        return <Badge className="bg-green-500 text-white">OK</Badge>;
      default:
        return <Badge variant="outline">Desconocido</Badge>;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'toolsNotInstalled':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'toolsOld':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'toolsNotRunning':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'toolsOk':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const clearFilters = () => {
    setFilters({ search: '', status: '' });
    setActiveStatFilter('issues');
  };

  const hasFilters = filters.search || filters.status;

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`p-4 cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold">Reporte VMware Tools</h1>
          <p className="text-sm text-muted-foreground">VMs con tools no instaladas, desactualizadas o detenidas</p>
        </div>

        {/* Stats - Clickeable Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'issues' ? 'border-primary ring-1 ring-primary/20' : ''}`}
              onClick={() => handleStatFilter('issues')}
              data-testid="stat-issues"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-500">{stats.with_issues || 0}</p>
                <p className="text-xs text-muted-foreground">Con Problemas</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'not_installed' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`}
              onClick={() => handleStatFilter('not_installed')}
              data-testid="stat-not-installed"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-500">{stats.not_installed || 0}</p>
                <p className="text-xs text-muted-foreground">No Instalado</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'old' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`}
              onClick={() => handleStatFilter('old')}
              data-testid="stat-old"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-500">{stats.old || 0}</p>
                <p className="text-xs text-muted-foreground">Desactualizado</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-orange-500/50 ${activeStatFilter === 'not_running' ? 'border-orange-500 ring-1 ring-orange-500/20' : ''}`}
              onClick={() => handleStatFilter('not_running')}
              data-testid="stat-not-running"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-orange-500">{stats.not_running || 0}</p>
                <p className="text-xs text-muted-foreground">No Corriendo</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'ok' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`}
              onClick={() => handleStatFilter('ok')}
              data-testid="stat-ok"
            >
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-500">{stats.ok || 0}</p>
                <p className="text-xs text-muted-foreground">OK</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.total || 0}</p>
                <p className="text-xs text-muted-foreground">Total VMs</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar VM o servidor..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setActiveStatFilter('');
                  }}
                  className="h-9"
                  data-testid="tools-search-input"
                />
              </div>
              <Select 
                value={filters.status || "issues"} 
                onValueChange={(v) => {
                  setFilters(prev => ({ ...prev, status: v === "issues" ? "" : v }));
                  setActiveStatFilter(v);
                }}
              >
                <SelectTrigger className="w-44 h-9" data-testid="tools-status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="issues">Con Problemas</SelectItem>
                  <SelectItem value="not_installed">No Instalado</SelectItem>
                  <SelectItem value="old">Desactualizado</SelectItem>
                  <SelectItem value="not_running">No Corriendo</SelectItem>
                  <SelectItem value="ok">OK</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && filters.status !== '' && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="w-full text-sm" data-testid="tools-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="tools_status" className="text-center justify-center">TOOLS</SortableHeader>
                  <SortableHeader field="tools_version" className="text-left">VERSIÓN</SortableHeader>
                  <SortableHeader field="power_state" className="text-center justify-center">ESTADO VM</SortableHeader>
                  <SortableHeader field="guest_os" className="text-left">SISTEMA OPERATIVO</SortableHeader>
                  <SortableHeader field="server_hostname" className="text-left">SERVIDOR</SortableHeader>
                  <th className="text-left p-4">PLATAFORMA</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : vms.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">No hay VMs con este estado de tools</td></tr>
                ) : vms.map(vm => (
                  <tr key={vm.id} className="border-b hover:bg-muted/30" data-testid={`tools-row-${vm.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{vm.name}</span>
                      </div>
                      {vm.ip_address && (
                        <p className="text-xs text-muted-foreground ml-6 font-mono">{vm.ip_address}</p>
                      )}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {getStatusIcon(vm.tools_status)}
                        {getStatusBadge(vm.tools_status)}
                      </div>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {vm.tools_version || '-'}
                    </td>
                    <td className="p-4 text-center">
                      {vm.power_state === 'poweredOn' ? (
                        <div className="flex items-center justify-center gap-1">
                          <Power className="h-4 w-4 text-green-500" />
                          <span className="text-xs">On</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <PowerOff className="h-4 w-4 text-red-500" />
                          <span className="text-xs">Off</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-sm">{vm.guest_os || '-'}</td>
                    <td className="p-4">
                      {vm.server_id ? (
                        <Link 
                          to={`/assets/servers?search=${encodeURIComponent(vm.server_hostname)}`}
                          className="flex items-center gap-1 text-blue-500 hover:underline text-sm"
                        >
                          <Link2 className="h-3 w-3" />
                          {vm.server_hostname}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">{vm.platform_host || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.pages} ({pagination.total} VMs)
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VirtToolsReport;
